import React, { useMemo, useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Button, Stack } from 'react-bootstrap';
import DataTable, { TableColumn } from 'react-data-table-component';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPencil, faTrash, faPlus } from '@fortawesome/free-solid-svg-icons';
import { selectIsHospital } from '@components/Auth';
import { ConfirmModal } from '@components/Base';
import { HealthClaimBillingItem } from '@models/HealthClaimBillingItem';
import { selectHealthClaim } from '../selector';
import {
  policyBenefitLimitsFetch,
  healthClaimBillingItemsFetch,
  healthClaimBillingItemDelete,
} from '../action';
import { BillingItemFormModal } from './billing-item-form-modal';
import { BillingItemEditModal } from './billing-item-edit-modal';

const BillingDetailsSection = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isHospital = useSelector(selectIsHospital);
  const { healthClaim, policyBenefitLimits, healthClaimBillingItems } = useSelector(selectHealthClaim);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState<HealthClaimBillingItem | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const grandTotal = (healthClaimBillingItems ?? []).reduce(
    (total, item) => total + Number(item.subtotalAmount),
    0,
  );

  const columns = useMemo(
    () =>
      [
        {
          name: t('billingDetails.subBenefit'),
          selector: (row) => row.subBenefit,
          width: '200px',
          wrap: true,
        },
        {
          name: t('billingDetails.itemName'),
          selector: (row) => row.itemName,
          width: '250px',
          wrap: true,
        },
        {
          name: t('billingDetails.price'),
          selector: (row) => Number(row.priceAmount).toFixed(2),
          width: '150px',
        },
        {
          name: t('billingDetails.quantity'),
          selector: (row) => row.quantity,
          width: '100px',
        },
        {
          name: t('billingDetails.discount'),
          selector: (row) => `${Number(row.discountPercentage).toFixed(2)}%`,
          width: '120px',
        },
        {
          name: t('billingDetails.subtotal'),
          selector: (row) => Number(row.subtotalAmount).toFixed(2),
          width: '150px',
        },
        {
          name: t('billingDetails.actions'),
          cell: (row) => {
            if (!isHospital) return null;

            return (
              <Stack direction="horizontal" gap={2}>
                <Button
                  size="sm"
                  variant="outline-primary"
                  onClick={() => {
                    setSelectedItem(row);
                    setShowEditModal(true);
                  }}
                >
                  <FontAwesomeIcon icon={faPencil} className="fa-fw" />
                </Button>
                <Button
                  size="sm"
                  variant="outline-danger"
                  onClick={() => {
                    setSelectedItem(row);
                    setShowConfirmModal(true);
                  }}
                >
                  <FontAwesomeIcon icon={faTrash} className="fa-fw" />
                </Button>
              </Stack>
            );
          },
        },
      ] as TableColumn<HealthClaimBillingItem>[],
    [isHospital, t],
  );

  const customStyles = {
    headCells: {
      style: {
        fontWeight: 'bold',
      },
    },
  };

  useEffect(() => {
    if (!healthClaim) return;
    if (healthClaim.policyBenefitTemplateId) {
      dispatch(policyBenefitLimitsFetch({ policyBenefitTemplateId: healthClaim.policyBenefitTemplateId }));
    }
    dispatch(healthClaimBillingItemsFetch({ healthClaimId: healthClaim.id }));
  }, [healthClaim?.id]);

  return (
    <section>
      <div className="d-flex flex-row justify-content-between align-items-center mb-5">
        <h5>{t('billingDetails.title')}</h5>
        {isHospital && (
          <Button onClick={() => setShowAddModal(true)}>
            <FontAwesomeIcon icon={faPlus} className="fa-fw me-2" />
            {t('billingDetails.addItems')}
          </Button>
        )}
      </div>

      <DataTable columns={columns} data={healthClaimBillingItems ?? []} customStyles={customStyles} fixedHeader />

      <Stack direction="horizontal" gap={5} className="mt-5">
        <Stack>
          <span>{t('billingDetails.grandTotal')}:</span>
          <h5>{grandTotal.toFixed(2)}</h5>
        </Stack>
      </Stack>

      {healthClaim && (
        <>
          <BillingItemFormModal
            healthClaimId={healthClaim.id}
            policyBenefitLimits={policyBenefitLimits ?? []}
            businessPartnerId={healthClaim.businessPartner?.id}
            show={showAddModal}
            onHide={() => setShowAddModal(false)}
          />

          <BillingItemEditModal
            healthClaimBillingItem={selectedItem}
            show={showEditModal}
            onHide={() => {
              setSelectedItem(null);
              setShowEditModal(false);
            }}
          />

          <ConfirmModal
            show={showConfirmModal}
            onHide={() => {
              setShowConfirmModal(false);
              setSelectedItem(null);
            }}
            onConfirm={() => {
              if (selectedItem) {
                dispatch(healthClaimBillingItemDelete({ id: selectedItem.id }));
              }
              setShowConfirmModal(false);
              setSelectedItem(null);
            }}
          />
        </>
      )}
    </section>
  );
};

export { BillingDetailsSection };
